import type { NodeType } from "@/types/domain";
import { fileKindOf, mimeTypeFromFilename } from "@/../convex/lib/files";

/**
 * Check if a URL points to an image based on file extension
 */
export function isImageUrl(url: string): boolean {
  try {
    return fileKindOf({ name: new URL(url).pathname }) === "image";
  } catch {
    return false;
  }
}

/**
 * Le node à créer pour un fichier collé ou déposé, d'après sa nature (cf.
 * `fileKindOf` : le type MIME, puis l'extension — les fichiers glissés depuis
 * certains explorateurs arrivent avec un `type` vide, et `text/plain` est
 * parfois annoncé pour un `.csv`).
 *
 * Les médias vont à leur lecteur, un CSV devient une table, un texte un
 * document ; le node `file` prend tout le reste (PDF, Word, Excel, zip…).
 * Une vidéo qu'aucun navigateur ne décode (`.mkv`) reste une vidéo : le node
 * sait le dire et proposer le téléchargement.
 */
export function resolveFileNodeType(file: {
  type: string;
  name: string;
}): NodeType {
  const lower = file.name.toLowerCase();
  // L'extension d'abord pour le CSV : il arrive annoncé `text/plain`.
  if (file.type === "text/csv" || lower.endsWith(".csv")) return "table";
  const kind = fileKindOf({ mime: file.type, name: file.name });
  if (kind === "image" || kind === "video" || kind === "audio") return kind;
  if (
    file.type === "text/markdown" ||
    file.type === "text/plain" ||
    /\.(md|markdown|txt)$/.test(lower)
  ) {
    return "blocknote";
  }
  return "file";
}

/**
 * Le type MIME à envoyer avec ce fichier : celui que le navigateur annonce
 * quand il en annonce un, sinon celui que dit l'extension.
 *
 * `file.type` est vide pour beaucoup de fichiers glissés depuis un
 * explorateur — un `.mov` ou un `.mkv` sorti du Finder, typiquement. Comme
 * `resolveUploadPolicy` refuse un type vide, l'upload échouait pour ces
 * fichiers avant même de partir. On retombe donc sur l'extension.
 */
export function mimeTypeForFile(file: { type: string; name: string }): string {
  if (file.type) return file.type;
  return mimeTypeFromFilename(file.name) ?? "";
}
